import { api } from '@/lib/api';
import type { ApplicationStatus } from '@/lib/intake/api';

export type Outcome = 'ACCEPTED' | 'DECLINED';

export interface ListItem {
  applicationId: string;
  reference: string;
  status: ApplicationStatus;
  outcome: Outcome | null;
  applicantEmail: string;
  applicantName: string | null;
  submittedAt: string;
  updatedAt: string;
  incompleteSections: number;
  files: number;
}

export interface ListPage {
  items: ListItem[];
  page: number;
  pageSize: number;
  total: number;
}

export interface Line {
  question: string;
  answer: string;
  kind: 'ANSWERED' | 'NOT_ANSWERED' | 'SKIPPED';
}

export interface Section {
  number: number;
  title: string;
  complete: boolean;
  lines: Line[];
}

export interface FileItem {
  fileId: string;
  requirementId: string;
  fileName: string;
  contentType: string;
  sizeBytes: number;
  scanStatus: 'PENDING' | 'CLEAN' | 'INFECTED' | 'FAILED';
  uploadedAt: string;
  downloadable: boolean;
}

export interface Requirement {
  id: string;
  title: string;
  highlighted: boolean;
  files: FileItem[];
}

export interface HistoryItem {
  from: ApplicationStatus;
  to: ApplicationStatus;
  reason: string | null;
  by: string;
  at: string;
}

export interface TrailItem {
  at: string;
  action: string;
  by: string;
  detail: string;
}

export interface Detail {
  applicationId: string;
  reference: string;
  status: ApplicationStatus;
  outcome: Outcome | null;
  applicantEmail: string;
  submittedAt: string;
  updatedAt: string;
  /** When its answers and files were deleted under the retention period. */
  purgedAt: string | null;
  catalogueVersion: number;
  banner: string;
  sections: Section[];
  documents: Requirement[];
  next: ApplicationStatus[];
  history: HistoryItem[];
  trail: TrailItem[];
}

export interface Search {
  status: ApplicationStatus | null;
  text: string;
  page: number;
}

export const review = {
  // A POST, so what is searched for — a name, an address — never sits in an address (A9).
  search: (search: Search) => api.post<ListPage>('/api/admin/applications/search', search),
  detail: (id: string) => api.get<Detail>(`/api/admin/applications/${id}`),
  downloadLink: (id: string, fileId: string) =>
    api.get<{ url: string; expiresAt: string }>(`/api/admin/applications/${id}/files/${fileId}`),
  move: (id: string, from: ApplicationStatus, to: ApplicationStatus, reason: string, outcome: Outcome | null) =>
    api.post<Detail>(`/api/admin/applications/${id}/status`, { from, to, reason, ...(outcome ? { outcome } : {}) }),
};

/** As staff say them. */
export const STATUS_LABELS: Record<ApplicationStatus, string> = {
  DRAFT: 'Draft',
  SUBMITTED: 'Submitted',
  IN_REVIEW: 'In review',
  INFORMATION_REQUESTED: 'Information requested',
  CLOSED: 'Closed',
};

export const OUTCOME_LABELS: Record<Outcome, string> = {
  ACCEPTED: 'Accepted as a client',
  DECLINED: 'Declined',
};

export function statusLabel(status: ApplicationStatus, outcome: Outcome | null): string {
  return status === 'CLOSED' && outcome ? `Closed: ${OUTCOME_LABELS[outcome].toLowerCase()}` : STATUS_LABELS[status];
}

/** The Badge tone for a status: what needs a reviewer stands out. */
export function statusTone(status: ApplicationStatus): 'accent' | 'neutral' | 'success' {
  if (status === 'SUBMITTED') return 'accent';
  if (status === 'CLOSED') return 'success';
  return 'neutral';
}
